import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { changeActivityQuery, fetchCaseAttachmentsQuery } from 'app/edozvola/cases/case-attachment/service/case-attachment.queries';
import ViewTableHeaderColumn from 'app/shared/view-table/view-table-header-column';
import { CaseAttachmentModel } from 'app/edozvola/cases/case-attachment/model/case-attachment.model';
import { convertDateFromServer } from 'app/shared/util/date-utils';
import CaseAttachmentDeleteModal from 'app/edozvola/cases/case-attachment/case-attachment-delete-modal';
import ManagableTable from 'app/shared/view-table/managable-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { downloadCaseAttachmentFile, getCaseAttachmentFile } from 'app/edozvola/cases/case-attachment/service/case-attachment.service';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { TABLE_QUERY_KEYS } from 'app/config/constants';
import { getInvalidRowsForTable } from 'app/shared/util/validation-errors.util';
import { ENTITY_TYPE } from 'app/shared/digital-signature/digital-signature.model';
import { useDigitalSignatureModal } from 'app/shared/digital-signature/DigitalSignatureModalContext';
import { ToggleWithLabels } from "edozvola-frontend-lib/dist/ui/components/toggle-with-labels";
import '../../../shared/view-table/view-table.scss';
import { getCaseContext } from 'app/shared/util/context-util';
import error = toast.error;
import { TooltipWrapper } from 'edozvola-frontend-lib/dist/ui/components/tooltipwrapper';
import { openFileInNewWindow } from 'app/shared/util/navigation-util';
import {faCircleNotch} from "@fortawesome/free-solid-svg-icons";
import { downloadFile } from 'app/shared/util/file-utils';

export interface ICaseAttachmentProps {
  onRowClick: (string, boolean) => void;
}

const CaseAttachmentTable = (props: ICaseAttachmentProps) => {
  const { id: webId } = useParams<{ id: string }>();
  const [modalOpened, setModalOpened] = useState(false);
  const [idCaseAttachmentToDelete, setIdCaseAttachmentToDelete] = useState<number>();
  const { sidebarItemPair } = getCaseContext();
  const { openDigitalSignatureModal } = useDigitalSignatureModal();
  const [previewAttachmentPendingAttachmentId, setPreviewAttachmentPendingAttachmentId] = useState<number>(null);

  const invalidRowIds = getInvalidRowsForTable(TABLE_QUERY_KEYS.CASE_ATTACHMENTS_TABLE);

  const { mutate: changeActivityMutation, isPending } = changeActivityQuery();

  const deleteCaseAttachment = (idCaseAttachment: number) => {
    setIdCaseAttachmentToDelete(idCaseAttachment);
    setModalOpened(true);
  };

  const cancelCaseAttachmentDeletion = () => {
    setIdCaseAttachmentToDelete(null);
    setModalOpened(false);
  };

  const previewFile = (idCaseAttachment: number) => {
    setPreviewAttachmentPendingAttachmentId(idCaseAttachment);
    getCaseAttachmentFile(idCaseAttachment)
      .then(data => {
        if (data) {
          openFileInNewWindow(data.url);
        }
      })
      .catch(err => showToast(error, 'Dogodila se greška prilikom preuzimanja dokumenta.'))
      .finally(() => setPreviewAttachmentPendingAttachmentId(null));
  };

  const downloadCaseAttachment = (idCaseAttachment: number, fileName: string) => {
    setPreviewAttachmentPendingAttachmentId(idCaseAttachment);
    downloadCaseAttachmentFile(idCaseAttachment)
      .then(data => {
        if (data) {
          const applicationType = data.fileType.type + '/' + data.fileType.subtype;
          downloadFile(fileName, data.contentData, applicationType);
        }
      })
      .catch(err => showToast(error, 'Dogodila se greška prilikom preuzimanja dokumenta.'))
      .finally(() => setPreviewAttachmentPendingAttachmentId(null));
  };

  const openDigitalSignature = (value: number) => {
    openDigitalSignatureModal({ entity: ENTITY_TYPE.CASE_ATTACHMENT, idEntity: value });
  };

  const renderCaseAttachmentTableRow = (value: CaseAttachmentModel, caseIndex: number, setFieldValue) => {
    return (
      <tr key={caseIndex} className={invalidRowIds.includes(value.idCaseAttachment) ? 'invalid-table-row' : ''}>
        <td>{value.requestAttachmentType}</td>
        <td>{value.name}</td>
        <td>{convertDateFromServer(value.caseAttachmentDate)}</td>
        <td>{value.createdBy}</td>
        <td>{value.lastDigitalSignatureBy}</td>
        <td>{value.active ? 'Aktivan' : 'Neaktivan'}</td>
        <td>
          <div className={'actions-container'}>
            {sidebarItemPair?.canViewDocument && value.contentType !== null && (
              <>
                <TooltipWrapper message={'Preuzmi dokument'}>
                  {previewAttachmentPendingAttachmentId === value.idCaseAttachment ? (
                    <FontAwesomeIcon icon={faCircleNotch} className={'icon-table'} spin={true} />
                  ) : (
                    <FontAwesomeIcon
                      icon={'download'}
                      className={'icon-table'}
                      aria-disabled={previewAttachmentPendingAttachmentId != null}
                      onClick={
                        previewAttachmentPendingAttachmentId
                          ? () => {}
                          : () => downloadCaseAttachment(value.idCaseAttachment, `${value.name}.${value.contentType.toLowerCase()}`)
                      }
                    />
                  )}
                </TooltipWrapper>
                {value.contentType === 'PDF' && (
                  <TooltipWrapper message={'Pregledaj dokument'}>
                    {previewAttachmentPendingAttachmentId === value.idCaseAttachment ? (
                      <FontAwesomeIcon icon={faCircleNotch} className={'icon-table'} spin={true} />
                    ) : (
                      <FontAwesomeIcon
                        icon={'file'}
                        className={'icon-table'}
                        aria-disabled={previewAttachmentPendingAttachmentId != null}
                        onClick={previewAttachmentPendingAttachmentId ? () => {} : () => previewFile(value.idCaseAttachment)}
                      />
                    )}
                  </TooltipWrapper>
                )}
              </>
            )}
            {sidebarItemPair?.canEdit && value.canEdit && (
              <TooltipWrapper message={'Uredi'}>
                <FontAwesomeIcon
                  icon={'pen-to-square'}
                  className={'icon-table'}
                  aria-disabled={previewAttachmentPendingAttachmentId != null}
                  onClick={previewAttachmentPendingAttachmentId ? () => {} : () => props.onRowClick(value.idCaseAttachment, false)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canSign && value.canSign && (
              <TooltipWrapper message={'Potpiši'}>
                <FontAwesomeIcon
                  icon={'pencil'}
                  className={'icon-table'}
                  onClick={previewAttachmentPendingAttachmentId ? () => {} : () => openDigitalSignature(value.idCaseAttachment)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canDelete && value.canDelete && (
              <TooltipWrapper message={'Obriši'}>
                <FontAwesomeIcon
                  icon={'trash-alt'}
                  className={'icon-table'}
                  onClick={previewAttachmentPendingAttachmentId ? () => {} : () => deleteCaseAttachment(value.idCaseAttachment)}
                />
              </TooltipWrapper>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <ManagableTable
        initialPaginationColumn={'caseAttachmentDate'}
        initialSortDirection="desc"
        autoLayout
        renderTableRow={renderCaseAttachmentTableRow}
        fetchDataQuery={fetchCaseAttachmentsQuery}
        fetchDataProps={webId}
        noDataMessage={'Nema priloga.'}
      >
        <ViewTableHeaderColumn renderSort sortKey={'requestAttachmentType'} columnLabels={['Vrsta priloga']} width={'300px'} />
        <ViewTableHeaderColumn renderSort sortKey={'name'} columnLabels={['Naziv priloga']} />
        <ViewTableHeaderColumn renderSort sortKey={'caseAttachmentDate'} columnLabels={['Datum unosa']} />
        <ViewTableHeaderColumn renderSort sortKey={'createdBy'} columnLabels={['Unio']} />
        <ViewTableHeaderColumn renderSort sortKey={'lastDigitalSignatureBy'} columnLabels={['Digitalno potpisao/la']} />
        <ViewTableHeaderColumn renderSort sortKey={'active'} columnLabels={['Status']} width={'100px'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Akcije']} width={'135px'} />
      </ManagableTable>
      <CaseAttachmentDeleteModal
        isModalOpen={modalOpened}
        closeModalCallback={cancelCaseAttachmentDeletion}
        idCaseAttachment={idCaseAttachmentToDelete}
      />
    </>
  );
};

export default CaseAttachmentTable;
